import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useUserSession } from './UserSessionContext';

const ChannelScopeContext = createContext(null);

export function ChannelScopeProvider({ children }) {
  const {
    isAdmin: integrationIsAdmin,
    userAssistants,
    platformAssistants,
    assistantsLoaded,
  } = useUserSession();

  const [scopeTab, setScopeTab] = useState('personal');

  const assistantById = useMemo(() => {
    const m = new Map();
    userAssistants.forEach((a) => m.set(a.id, a));
    platformAssistants.forEach((a) => {
      if (!m.has(a.id)) m.set(a.id, a);
    });
    return m;
  }, [userAssistants, platformAssistants]);

  const assistantsForTab = useMemo(() => {
    if (scopeTab === 'platform') {
      return integrationIsAdmin ? platformAssistants : [];
    }
    return userAssistants;
  }, [scopeTab, integrationIsAdmin, platformAssistants, userAssistants]);

  const assistantIdsInScope = useMemo(() => {
    let rows = assistantsForTab;
    if (scopeTab === 'personal') rows = rows.filter((a) => !a.group_id);
    else if (scopeTab === 'group') rows = rows.filter((a) => a.group_id);
    return new Set(rows.map((a) => a.id));
  }, [assistantsForTab, scopeTab]);

  const assistantsInScope = useMemo(() => {
    let rows = assistantsForTab;
    if (scopeTab === 'personal') return rows.filter((a) => !a.group_id);
    if (scopeTab === 'group') return rows.filter((a) => a.group_id);
    return rows;
  }, [assistantsForTab, scopeTab]);

  const buildScopeTabOptions = useCallback(
    (rows, assistantIdField = 'assistant_id') => {
      const countForScope = (tab) => {
        if (tab === 'platform' && !integrationIsAdmin) return 0;
        const assistantsSource =
          tab === 'platform' ? platformAssistants : userAssistants;
        let scoped = assistantsSource;
        if (tab === 'personal') scoped = scoped.filter((a) => !a.group_id);
        else if (tab === 'group') scoped = scoped.filter((a) => a.group_id);
        const ids = new Set(scoped.map((a) => a.id));
        return rows.filter((r) => ids.has(r[assistantIdField])).length;
      };
      return [
        { value: 'personal', text: 'Personal', count: countForScope('personal') },
        { value: 'group', text: 'Group', count: countForScope('group') },
        { value: 'platform', text: 'Platform', count: countForScope('platform') },
      ];
    },
    [userAssistants, platformAssistants, integrationIsAdmin]
  );

  const filterRowsByScope = useCallback(
    (rows, filterAssistantId = null, assistantIdField = 'assistant_id') => {
      if (!assistantsLoaded) return [];
      if (scopeTab === 'platform' && !integrationIsAdmin) return [];
      let result = rows.filter(
        (r) =>
          assistantById.has(r[assistantIdField]) &&
          assistantIdsInScope.has(r[assistantIdField])
      );
      if (filterAssistantId) {
        result = result.filter((r) => r[assistantIdField] === filterAssistantId);
      }
      return result;
    },
    [assistantById, assistantIdsInScope, scopeTab, integrationIsAdmin, assistantsLoaded]
  );

  const value = useMemo(
    () => ({
      integrationIsAdmin,
      scopeTab,
      setScopeTab,
      assistantById,
      buildScopeTabOptions,
      filterRowsByScope,
      userAssistants,
      platformAssistants,
      assistantsInScope,
      assistantsLoaded,
    }),
    [
      integrationIsAdmin,
      scopeTab,
      assistantById,
      buildScopeTabOptions,
      filterRowsByScope,
      userAssistants,
      platformAssistants,
      assistantsInScope,
      assistantsLoaded,
    ]
  );

  return (
    <ChannelScopeContext.Provider value={value}>{children}</ChannelScopeContext.Provider>
  );
}

export function useChannelScope() {
  const ctx = useContext(ChannelScopeContext);
  if (!ctx) {
    throw new Error('useChannelScope must be used within ChannelScopeProvider');
  }
  return ctx;
}
